/**
 * Calendar deep-link URL builders for Google Calendar and Outlook, plus a
 * browser-safe `.ics` builder for Apple Calendar / desktop Outlook / other clients.
 */

const GOOGLE_CALENDAR_HOME = "https://calendar.google.com/";
const GOOGLE_CALENDAR_COMPOSE = "https://calendar.google.com/calendar/render";
// Microsoft 365 / Outlook on the web (outlook.office.com). We previously offered a
// separate Outlook.com (consumer) option but removed it — outlook.live.com bounces
// work-account users to a product-marketing page, and consumer users can fall back to
// the Apple Calendar (.ics) download.
const OFFICE365_CALENDAR_HOME = "https://outlook.office.com/calendar/";
const OFFICE365_CALENDAR_COMPOSE = "https://outlook.office.com/calendar/0/deeplink/compose";

export interface CalendarEventInput {
  title: string;
  /** ISO start date YYYY-MM-DD. When null, functions return a bare calendar home URL. */
  date: string | null;
  /** ISO end date YYYY-MM-DD. Null/omitted → same as `date` (single-day event). */
  endDate?: string | null;
  /** 24-hour HH:MM. Null → all-day event. */
  startTime: string | null;
  /** 24-hour HH:MM. Null → same as startTime (timed) or omitted (all-day). */
  endTime: string | null;
  location?: string | null;
  /** IANA timezone, e.g. "America/New_York". Null → floating time. */
  timeZone?: string | null;
  /** Optional free-text body for the event. Null/omitted → no DESCRIPTION line. ICS only; URL builders ignore it. */
  description?: string | null;
}

/** Normalize a time string: strip whitespace, accept HH:MM or HH:MM:SS, return HH:MM. Null/empty/malformed → null. */
function normalizeTime(t: string | null | undefined): string | null {
  if (t == null) return null;
  const trimmed = t.trim();
  if (!trimmed) return null;
  const m = trimmed.match(/^(\d{2}):(\d{2})(?::\d{2})?$/);
  return m ? `${m[1]}:${m[2]}` : null;
}

/** True when the input looks like the user meant a time but typed it wrong (not null, not empty, not HH:MM). */
function isMalformedTimeString(t: string | null | undefined): boolean {
  if (t == null) return false;
  const trimmed = t.trim();
  if (!trimmed) return false;
  return !/^(\d{2}):(\d{2})(?::\d{2})?$/.test(trimmed);
}

/**
 * Validate a YYYY-MM-DD string and reject impossible dates. Rejects values like "2026-13-45"
 * (out-of-range month/day) and "2026-02-30" (Date.UTC silently normalizes February 30 to
 * March 2; the round-trip check catches that). Returns parsed parts or null.
 */
function parseIsoDate(s: string | null | undefined): { year: number; month: number; day: number } | null {
  if (typeof s !== "string") return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const dt = new Date(Date.UTC(year, month - 1, day));
  if (dt.getUTCFullYear() !== year || dt.getUTCMonth() !== month - 1 || dt.getUTCDate() !== day) return null;
  return { year, month, day };
}

/** Return YYYY-MM-DD for the next calendar day. Returns null when the input can't be parsed. */
function nextDate(date: string): string | null {
  const p = parseIsoDate(date);
  if (!p) return null;
  const next = new Date(Date.UTC(p.year, p.month - 1, p.day + 1));
  const yr = String(next.getUTCFullYear()).padStart(4, "0");
  const mo = String(next.getUTCMonth() + 1).padStart(2, "0");
  const da = String(next.getUTCDate()).padStart(2, "0");
  return `${yr}-${mo}-${da}`;
}

/** Add one hour to a HH:MM time on a YYYY-MM-DD date. Wraps to the next calendar day if the hour overflows. */
function addOneHour(date: string, time: string): { date: string; time: string } {
  const dm = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const tm = time.match(/^(\d{2}):(\d{2})$/);
  if (!dm || !tm) return { date, time };
  const totalMin = Number(tm[1]) * 60 + Number(tm[2]) + 60;
  const newH = Math.floor(totalMin / 60);
  const newM = totalMin % 60;
  if (newH < 24) {
    return { date, time: `${String(newH).padStart(2, "0")}:${String(newM).padStart(2, "0")}` };
  }
  const next = new Date(Date.UTC(Number(dm[1]), Number(dm[2]) - 1, Number(dm[3]) + 1));
  const yr = String(next.getUTCFullYear()).padStart(4, "0");
  const moStr = String(next.getUTCMonth() + 1).padStart(2, "0");
  const dStr = String(next.getUTCDate()).padStart(2, "0");
  return {
    date: `${yr}-${moStr}-${dStr}`,
    time: `${String(newH - 24).padStart(2, "0")}:${String(newM).padStart(2, "0")}`,
  };
}

/** Convert YYYY-MM-DD + optional normalized HH:MM to Google's compact format: YYYYMMDDTHHmm00 or YYYYMMDD. */
function toGCalDateTime(date: string, time: string | null): string {
  const d = date.replace(/-/g, "");
  if (!time) return d;
  return `${d}T${time.replace(":", "")}00`;
}

/** Return the next calendar day as YYYYMMDD (exclusive end for Google all-day events). Returns null on malformed input. */
function nextDayCompact(date: string): string | null {
  const p = parseIsoDate(date);
  if (!p) return null;
  const next = new Date(Date.UTC(p.year, p.month - 1, p.day + 1));
  const yr = String(next.getUTCFullYear()).padStart(4, "0");
  const mo = String(next.getUTCMonth() + 1).padStart(2, "0");
  const da = String(next.getUTCDate()).padStart(2, "0");
  return `${yr}${mo}${da}`;
}

/**
 * Build a Google Calendar "Add to Calendar" compose URL.
 *
 * @param event - Calendar event details.
 * @returns Full URL for the Google Calendar compose dialog.
 */
export function buildGoogleCalendarUrl(event: CalendarEventInput): string {
  if (!event.date) return GOOGLE_CALENDAR_HOME;
  // Reject any malformed/impossible start date — silent "NaNNaNNaN" in the URL is worse than
  // bouncing the user to the calendar home.
  if (!parseIsoDate(event.date)) return GOOGLE_CALENDAR_HOME;

  // Graceful fallback: if endDate is malformed, treat it as missing (single-day event) rather
  // than letting NaN propagate into the URL. ICS chooses strict (null); URL builders are lenient.
  const endDateRaw = event.endDate ?? event.date;
  const endDate = parseIsoDate(endDateRaw) ? endDateRaw : event.date;
  const normStart = normalizeTime(event.startTime);
  const normEnd = normalizeTime(event.endTime);
  const hasTimes = normStart !== null;
  let dates: string;
  if (hasTimes) {
    const start = toGCalDateTime(event.date, normStart);
    const endStamp = normEnd
      ? toGCalDateTime(endDate, normEnd)
      : (() => {
          const bumped = addOneHour(endDate, normStart);
          return toGCalDateTime(bumped.date, bumped.time);
        })();
    dates = `${start}/${endStamp}`;
  } else {
    // endDate is validated above, so nextDayCompact won't return null here. Fall back defensively.
    const compact = nextDayCompact(endDate) ?? event.date.replace(/-/g, "");
    dates = `${event.date.replace(/-/g, "")}/${compact}`;
  }

  let url = `${GOOGLE_CALENDAR_COMPOSE}?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${dates}`;
  if (event.location) url += `&location=${encodeURIComponent(event.location)}`;
  // Gate ctz with the same safety check as the Outlook/ICS builders — pasted labels like "PST"
  // or values with trailing whitespace silently cause Google to fall back to viewer-local time.
  if (hasTimes && isSafeIanaTimeZone(event.timeZone)) url += `&ctz=${encodeURIComponent(event.timeZone)}`;
  return url;
}

/** Format a YYYY-MM-DD + optional HH:MM for Outlook's `startdt`/`enddt` params, optionally with a UTC offset suffix. */
function toOutlookDateTime(date: string, time: string | null, offset: string): string {
  return time ? `${date}T${time}:00${offset}` : date;
}

/**
 * Return the UTC offset for an IANA timezone on a specific date as "±HH:MM" (DST-aware).
 * Outlook's compose URL has no `ctz` equivalent, but it does parse ISO 8601 dates with an
 * explicit offset — appending one lets Outlook display the event in the correct local time
 * for the viewer regardless of which zone they're in.
 *
 * @param iana - IANA timezone name (e.g., "America/Los_Angeles").
 * @param date - Reference date YYYY-MM-DD; the offset is computed at noon UTC of this day.
 * @returns The offset string (e.g., "-08:00"), or null when the zone is invalid or the
 *   platform doesn't support longOffset formatting.
 */
function getUtcOffsetForZone(iana: string, date: string): string | null {
  const dm = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!dm) return null;
  try {
    // Noon UTC sits inside the calendar date in every zone — safely away from DST-spring edges.
    const atDate = new Date(Date.UTC(Number(dm[1]), Number(dm[2]) - 1, Number(dm[3]), 12));
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: iana,
      timeZoneName: "longOffset",
    }).formatToParts(atDate);
    const tzName = parts.find((p) => p.type === "timeZoneName")?.value;
    if (!tzName) return null;
    if (tzName === "GMT") return "+00:00";
    const m = tzName.match(/^GMT([+-]\d{2}:\d{2})$/);
    return m?.[1] ?? null;
  } catch {
    return null;
  }
}

/**
 * Build an Outlook-style "Add to Calendar" compose URL. Outlook.com and Office 365 share
 * the same compose query-param shape, so the only difference is the host (live.com vs
 * office.com). The home URL is returned when `event.date` is null.
 *
 * Both hosts require `path=/calendar/action/compose` and `rru=addevent` to route the
 * deeplink — without them, Microsoft bounces the user to a product-marketing page even
 * when they're already signed in. (Standard pattern used by atcb-action / addtocalendar.com.)
 */
function buildOutlookLikeUrl(homeUrl: string, composeUrl: string, event: CalendarEventInput): string {
  if (!event.date) return homeUrl;
  if (!parseIsoDate(event.date)) return homeUrl;

  // Graceful fallback for malformed endDate — see buildGoogleCalendarUrl for rationale.
  const endDateRaw = event.endDate ?? event.date;
  const endDate = parseIsoDate(endDateRaw) ? endDateRaw : event.date;
  const normStart = normalizeTime(event.startTime);
  const normEnd = normalizeTime(event.endTime);
  // Append a UTC offset (e.g., "-07:00") to timed startdt/enddt when an IANA zone is set,
  // so Outlook displays the event in the correct local time for the viewer regardless of
  // which zone they're in. All-day events stay zoneless per RFC 5545.
  const offset =
    normStart && isSafeIanaTimeZone(event.timeZone) ? (getUtcOffsetForZone(event.timeZone, event.date) ?? "") : "";
  const startdt = toOutlookDateTime(event.date, normStart, offset);
  let enddt: string;
  if (!normStart) {
    // All-day VEVENT — Outlook treats enddt as exclusive (same as ICS DTEND and Google
    // dates= ranges), so bump by one day to preserve the user's "runs through endDate"
    // intent. Without this, the form would show endDate-1 as the last day.
    // endDate is parseIsoDate-validated above, so nextDate returns non-null here; fall back to
    // endDate itself only if a future refactor breaks that invariant.
    enddt = toOutlookDateTime(nextDate(endDate) ?? endDate, null, "");
  } else if (!normEnd) {
    const bumped = addOneHour(endDate, normStart);
    enddt = toOutlookDateTime(bumped.date, bumped.time, offset);
  } else {
    enddt = toOutlookDateTime(endDate, normEnd, offset);
  }

  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: event.title,
    startdt,
    enddt,
  });
  if (event.location) params.set("location", event.location);
  if (!normStart) params.set("allday", "true");
  // URLSearchParams form-encodes spaces as "+"; convert to %20 for cross-client safety
  // and to match the encoding used by the Google URL builder above.
  return `${composeUrl}?${params.toString().replace(/\+/g, "%20")}`;
}

/**
 * Build a Microsoft 365 / Office 365 compose URL (outlook.office.com).
 *
 * @param event - Calendar event details.
 * @returns Full URL for the Office 365 calendar compose dialog. Use this for users on
 *   work accounts; consumer Outlook.com users should use `buildOutlookCalendarUrl`.
 */
export function buildOffice365CalendarUrl(event: CalendarEventInput): string {
  return buildOutlookLikeUrl(OFFICE365_CALENDAR_HOME, OFFICE365_CALENDAR_COMPOSE, event);
}

/** Escape values per RFC 5545 §3.3.11 (TEXT property). Order matters: backslash first. */
function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/[\r\n]+/g, "\\n");
}

/** Browser-safe non-cryptographic hash → 8-char base36. Used only for ICS UID stability across re-downloads. */
function hashUid(input: string): string {
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) + hash) ^ input.charCodeAt(i);
  }
  return (hash >>> 0).toString(36).padStart(8, "0");
}

/** Strict IANA timezone shape check — defends against any CRLF / `;` / `:` that would break ICS framing. */
function isSafeIanaTimeZone(tz: string | null | undefined): tz is string {
  return typeof tz === "string" && /^[A-Za-z][A-Za-z0-9+_/-]{0,63}$/.test(tz);
}

/**
 * Build a `METHOD:PUBLISH` ICS payload for a self-download "add to my calendar" flow.
 * Returns null when required fields (date, parseable times) are missing or malformed.
 *
 * @param event - Calendar event details.
 * @returns RFC 5545 text payload, or null when not buildable.
 */
export function buildIcsContent(event: CalendarEventInput): string | null {
  if (!event.date) return null;
  const startParts = parseIsoDate(event.date);
  if (!startParts) return null;
  const endDateRaw = event.endDate ?? event.date;
  const endParts = parseIsoDate(endDateRaw);
  if (!endParts) return null;

  // Reject backwards date ranges — calendar clients render swapped DTSTART/DTEND inconsistently
  // (Google drops the event, Apple shows a 0-minute event on the start day). Compare on the raw
  // date strings since both are confirmed YYYY-MM-DD at this point.
  if (endDateRaw < event.date) return null;

  const yyyy = String(startParts.year).padStart(4, "0");
  const mm = String(startParts.month).padStart(2, "0");
  const dd = String(startParts.day).padStart(2, "0");
  const eyyyyIn = String(endParts.year).padStart(4, "0");
  const emmIn = String(endParts.month).padStart(2, "0");
  const eddIn = String(endParts.day).padStart(2, "0");

  // Reject inputs that *look like* a time but aren't HH:MM (e.g. "7pm", "9:00") — empty strings
  // are treated as "no time" via normalizeTime, but garbled non-empty values fail loudly.
  if (isMalformedTimeString(event.startTime) || isMalformedTimeString(event.endTime)) return null;

  const normStart = normalizeTime(event.startTime);
  const normEnd = normalizeTime(event.endTime);

  // On a single-day timed event, end-time must be after start-time. Multi-day events are
  // allowed to "end" earlier in the day on a later date (e.g., 5pm Day 1 → 9am Day 3).
  if (normStart && normEnd && endDateRaw === event.date && normEnd <= normStart) return null;

  let dtStartLine: string;
  let dtEndLine: string;

  if (!normStart) {
    // All-day VEVENT — DATE-only values are zoneless per RFC 5545; DTEND is exclusive.
    const next = new Date(Date.UTC(endParts.year, endParts.month - 1, endParts.day + 1));
    const eyyyy = String(next.getUTCFullYear()).padStart(4, "0");
    const emm = String(next.getUTCMonth() + 1).padStart(2, "0");
    const edd = String(next.getUTCDate()).padStart(2, "0");
    dtStartLine = `DTSTART;VALUE=DATE:${yyyy}${mm}${dd}`;
    dtEndLine = `DTEND;VALUE=DATE:${eyyyy}${emm}${edd}`;
  } else {
    const startStamp = `${yyyy}${mm}${dd}T${normStart.replace(":", "")}00`;
    let endStampDateCompact: string;
    let endStampTimeCompact: string;
    if (normEnd) {
      endStampDateCompact = `${eyyyyIn}${emmIn}${eddIn}`;
      endStampTimeCompact = `${normEnd.replace(":", "")}00`;
    } else {
      const bumped = addOneHour(endDateRaw, normStart);
      endStampDateCompact = bumped.date.replace(/-/g, "");
      endStampTimeCompact = `${bumped.time.replace(":", "")}00`;
    }
    const tzidPrefix = isSafeIanaTimeZone(event.timeZone) ? `;TZID=${event.timeZone}` : "";
    dtStartLine = `DTSTART${tzidPrefix}:${startStamp}`;
    dtEndLine = `DTEND${tzidPrefix}:${endStampDateCompact}T${endStampTimeCompact}`;
  }

  const nowStamp = new Date()
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}/, "");
  const uidSeed = [
    event.title,
    event.date,
    event.endDate ?? event.date,
    normStart ?? "",
    normEnd ?? "",
    event.location ?? "",
  ].join("|");
  const uid = `${hashUid(uidSeed)}@nowadays.ai`;

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Nowadays//Landing Page Calendar//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${nowStamp}`,
    dtStartLine,
    dtEndLine,
    `SUMMARY:${escapeIcsText(event.title)}`,
    event.location ? `LOCATION:${escapeIcsText(event.location)}` : null,
    event.description ? `DESCRIPTION:${escapeIcsText(event.description)}` : null,
    "STATUS:CONFIRMED",
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter((line): line is string => line !== null);

  return lines.join("\r\n");
}

/**
 * Discriminated union representing one calendar "Add to" option. `kind: "link"` opens
 * an external compose URL in a new tab; `kind: "action"` triggers a local download.
 * Centralized so both the hero dropdown and the success-view buttons render the same
 * set of providers in the same order.
 */
export type CalendarOption =
  | { key: "google" | "office365"; label: string; kind: "link"; href: string }
  | { key: "ics"; label: string; kind: "action"; onClick: () => void };

/**
 * Build the canonical list of calendar "Add to" options for an event. Surfaces consume
 * this and render their own chrome — the URL/handler wiring stays in one place.
 *
 * @param event - Calendar event details.
 * @returns Ordered list of provider options.
 */
export function getCalendarOptions(event: CalendarEventInput): CalendarOption[] {
  return [
    { key: "google", label: "Google Calendar", kind: "link", href: buildGoogleCalendarUrl(event) },
    { key: "office365", label: "Outlook", kind: "link", href: buildOffice365CalendarUrl(event) },
    { key: "ics", label: "Apple Calendar (.ics)", kind: "action", onClick: () => downloadIcs(event) },
  ];
}

/**
 * Trigger a browser download of an `.ics` file for the given event.
 * No-op when the event is not buildable.
 *
 * @param event - Calendar event details.
 */
export function downloadIcs(event: CalendarEventInput): void {
  const content = buildIcsContent(event);
  if (!content) return;
  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  // Only strip filesystem-illegal characters (Windows: \\ / : * ? " < > | and C0 controls);
  // keep Unicode so non-Latin event titles ('東京ミートアップ') don't collapse to '____.ics'.
  // Trim leading/trailing dots+spaces (Windows trims them, which can produce an unexpected
  // filename like '.ics'). Cap at 64 chars to stay friendly across filesystems.
  const stripped = (event.title || "event").replace(/[\\/:*?"<>|\x00-\x1f]+/g, "_").trim();
  const trimmedEdges = stripped.replace(/^[. ]+|[. ]+$/g, "");
  const safeName = trimmedEdges.slice(0, 64) || "event";
  a.download = `${safeName}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
